import type { AuditCrawl, AuditCrawlInfo } from "@/lib/api/types";

import { formatCount, formatDate } from "./format";

type Severity = AuditCrawl["issues"][number]["severity"];

export const SEVERITY_LABEL: Record<Severity, string> = { high: "Hoch", mid: "Mittel", low: "Niedrig" };
export const SEVERITY_CLASS: Record<Severity, string> = {
  high: "bg-danger-bg text-danger",
  mid: "bg-warning-bg text-warning",
  low: "bg-info-bg text-info",
};

export const CATEGORIES = ["Alle", "SEO-Basics", "GEO"] as const;
export type Category = (typeof CATEGORIES)[number];

/** Veränderung zum vorigen Crawl: „+12“, „−9“, „±0“, ohne Vergleich ein Strich. */
export function formatDelta(delta: number | null): string {
  if (delta === null) return "–";
  if (delta === 0) return "±0";
  return `${delta > 0 ? "+" : "−"}${formatCount(Math.abs(delta))}`;
}

export function crawlLabel(crawl: AuditCrawlInfo): string {
  return `${formatDate(crawl.created_at)} · ${formatCount(crawl.url_count)} URLs · ${crawl.filename}`;
}

/** Spalten des Exports, ohne die eine Regel entfällt, in der Schreibweise von Screaming Frog. */
export const COLUMN_LABEL: Record<string, string> = {
  x_robots: "X-Robots-Tag 1",
  outlinks: "Outlinks",
  canonical: "Canonical Link Element 1",
  title: "Title 1",
  meta_description: "Meta Description 1",
  h1: "H1-1",
  h1_second: "H1-2",
  meta_robots: "Meta Robots 1",
  depth: "Crawl Depth",
  inlinks: "Unique Inlinks",
  word_count: "Word Count",
  response_time: "Response Time",
  last_modified: "Last Modified",
  redirect_url: "Redirect URL",
  indexability: "Indexability",
  content_type: "Content Type",
};

/** Spalten, die im Export fehlen und eine Regel kosten. `x_robots` und `outlinks` braucht keine Regel. */
export function relevantMissing(missing: string[]): string[] {
  return missing.filter((key) => key !== "x_robots" && key !== "outlinks" && key !== "indexability_status");
}

export function llmsLine(llms: NonNullable<AuditCrawl["llms"]>): string {
  if (!llms.present) {
    if (llms.error) return `llms.txt nicht prüfbar: ${llms.error}`;
    return `llms.txt: nicht vorhanden${llms.status ? ` (HTTP ${llms.status})` : ""}`;
  }
  const parts = [`llms.txt: vorhanden, ${formatCount(llms.entries ?? 0)} Einträge`];
  if (llms.has_title === false) parts.push("ohne Titelzeile (# …)");
  if ((llms.broken?.length ?? 0) > 0) parts.push(`${formatCount(llms.broken?.length ?? 0)} Ziel-URLs mit Fehlerstatus`);
  if ((llms.not_crawled ?? 0) > 0) parts.push(`${formatCount(llms.not_crawled ?? 0)} Ziele nicht im Crawl`);
  return parts.join(" · ");
}

export function issuesIn(crawl: AuditCrawl, category: Category) {
  return crawl.issues.filter((issue) => category === "Alle" || issue.category === category);
}
